<main class="smart-retail-page">
  <section class="smart-hero">
    <div class="hero-grid"></div>
    <div class="hero-orb hero-orb-one"></div>
    <div class="hero-orb hero-orb-two"></div>

    <div class="hero-copy">
      <span class="eyebrow">Smart Assets</span>
      <h1>Smart Assets for an Intelligent, Connected World</h1>
      <p>Discover, deploy, and manage AI-enabled equipment—from robotics and drones to automated systems, intelligent infrastructure, and transportation assets.</p>
      <div class="hero-actions">
        <a routerLink="/contact" [queryParams]="{ source: 'retail-smart-assets', type: 'retail' }" class="btn btn-primary">Explore Smart Assets</a>
        <a routerLink="/holdings" class="btn btn-secondary">Explore Managed Assets</a>
      </div>
    </div>

    <div class="hero-visual" aria-label="ANYSPACE smart asset network">
      <div class="asset-node node-drone"><span>DR</span><small>Drone</small></div>
      <div class="asset-node node-robot"><span>RB</span><small>Robotics</small></div>
      <div class="asset-node node-fleet"><span>FL</span><small>Fleet</small></div>
      <div class="agent-console">
        <span class="console-kicker">ANYSPACE</span>
        <h2>Agent Server</h2>
        <div class="console-grid">
          <div><strong>Assets</strong><span>Connected</span></div>
          <div><strong>Agents</strong><span>Coordinated</span></div>
          <div><strong>Data</strong><span>Context</span></div>
          <div><strong>Workflows</strong><span>Automated</span></div>
          <div><strong>Monitoring</strong><span>Operational</span></div>
          <div><strong>Insights</strong><span>Actionable</span></div>
        </div>
      </div>
    </div>
  </section>

  <section class="page-section commercial-models" aria-labelledby="commercial-model-heading">
    <div class="section-intro">
      <span class="section-kicker">Five ways to adopt smart assets</span>
      <h2 id="commercial-model-heading">Choose the Right Commercial Model</h2>
      <p>Not every smart asset is bought the same way. ANYSPACE supports direct retail, professional sales, managed assets, managed transportation, and emerging technology discovery.</p>
    </div>

    <div class="model-grid">
      <article class="model-card store-card">
        <div class="model-art art-store"><span class="art-chip">AI</span><span class="art-ring"></span></div>
        <span class="model-number">01</span>
        <h3>Smart Asset Store</h3>
        <p>Buy selected smart assets directly online as product availability and sourcing allow.</p>
        <ul>
          <li>Drones and AI cameras</li>
          <li>3D printers and laser systems</li>
          <li>Robotic mowers and cleaners</li>
          <li>Edge AI and home AI servers</li>
        </ul>
        <a routerLink="/contact" [queryParams]="{ source: 'retail-smart-asset-store', type: 'retail' }" class="card-link">Explore Store Categories <span>→</span></a>
      </article>

      <article class="model-card professional-card">
        <div class="model-art art-professional"><span class="arm arm-one"></span><span class="arm arm-two"></span><span class="joint"></span></div>
        <span class="model-number">02</span>
        <h3>Professional Smart Assets</h3>
        <p>Higher-value equipment with expert guidance, integration planning, and support.</p>
        <ul>
          <li>CNC and production equipment</li>
          <li>Cobots and robotic arms</li>
          <li>LiDAR and inspection systems</li>
          <li>Commercial energy systems</li>
        </ul>
        <a routerLink="/contact" [queryParams]="{ source: 'retail-professional-assets', type: 'retail' }" class="card-link">Request a Quote <span>→</span></a>
      </article>

      <article class="model-card managed-card featured-model">
        <div class="model-art art-managed"><span class="box box-one"></span><span class="box box-two"></span><span class="bot bot-one"></span><span class="bot bot-two"></span></div>
        <span class="model-number">03</span>
        <h3>Managed Smart Assets</h3>
        <p>Combine hardware, connectivity, Agent Server coordination, monitoring, AI, and recurring services.</p>
        <ul>
          <li>Delivery and warehouse robots</li>
          <li>Smart vending and kiosks</li>
          <li>Commercial drones</li>
          <li>Agricultural automation</li>
        </ul>
        <a routerLink="/holdings" class="card-link">Explore Managed Assets <span>→</span></a>
      </article>

      <article class="model-card transport-card">
        <div class="model-art art-transport"><span class="road"></span><span class="vehicle truck"></span><span class="vehicle van"></span></div>
        <span class="model-number">04</span>
        <h3>Managed Transportation</h3>
        <p>Coordinate drones, mobile robots, autonomous systems, and commercial vehicles as managed fleets.</p>
        <ul>
          <li>Fleet coordination</li>
          <li>Asset and telemetry context</li>
          <li>Operational workflows</li>
          <li>Agent Server integration</li>
        </ul>
        <a routerLink="/transportation" class="card-link">Explore Transportation <span>→</span></a>
      </article>

      <article class="model-card emerging-card">
        <div class="model-art art-emerging"><span class="head"></span><span class="body"></span><span class="greenhouse"></span></div>
        <span class="model-number">05</span>
        <h3>Emerging Smart Assets</h3>
        <p>Explore next-generation technologies that may begin as content, partnerships, or commercial inquiries.</p>
        <ul>
          <li>Humanoid robots</li>
          <li>Construction robotics</li>
          <li>Robotic kitchens</li>
          <li>Autonomous boats and vertical farms</li>
        </ul>
        <a routerLink="/contact" [queryParams]="{ source: 'retail-emerging-assets', type: 'innovation' }" class="card-link">Explore Innovations <span>→</span></a>
      </article>
    </div>
  </section>

  <section class="page-section agent-section" aria-labelledby="agent-server-heading">
    <div class="section-intro compact-intro">
      <span class="section-kicker">The coordination layer</span>
      <h2 id="agent-server-heading">ANYSPACE Agent Server</h2>
      <p>Connect smart assets, transportation systems, and commercial real estate operations with AI agents, shared context, workflows, monitoring, and controlled actions.</p>
    </div>

    <div class="agent-flow">
      <article class="flow-step">
        <span class="flow-icon">01</span>
        <h3>Integrate</h3>
        <p>Connect assets, software, sensors, and approved data sources.</p>
      </article>
      <span class="flow-arrow" aria-hidden="true">→</span>
      <article class="flow-step">
        <span class="flow-icon">02</span>
        <h3>Understand Context</h3>
        <p>Give agents operational state, events, requests, and asset context.</p>
      </article>
      <span class="flow-arrow" aria-hidden="true">→</span>
      <article class="flow-step">
        <span class="flow-icon">03</span>
        <h3>Coordinate</h3>
        <p>Route work among specialized agents, systems, and workflows.</p>
      </article>
      <span class="flow-arrow" aria-hidden="true">→</span>
      <article class="flow-step">
        <span class="flow-icon">04</span>
        <h3>Execute</h3>
        <p>Send authorized actions to connected systems and capture results.</p>
      </article>
      <article class="flow-results">
        <h3>Managed Outcomes</h3>
        <ul>
          <li>Connected asset visibility</li>
          <li>Coordinated operations</li>
          <li>Recurring management services</li>
          <li>Scalable smart-asset portfolios</li>
        </ul>
      </article>
    </div>
  </section>

  <section class="page-section category-section" aria-labelledby="category-heading">
    <div class="category-heading-row">
      <div>
        <span class="section-kicker">Smart asset categories</span>
        <h2 id="category-heading">From Individual Products to Managed Fleets</h2>
      </div>
      <a routerLink="/contact" [queryParams]="{ source: 'retail-all-categories', type: 'retail' }" class="text-link">Discuss a category →</a>
    </div>

    <div class="category-grid">
      <article class="category-card"><span class="category-icon">DR</span><h3>Drones</h3><p>Retail + managed fleets</p></article>
      <article class="category-card"><span class="category-icon">RM</span><h3>Robotic Mowers</h3><p>Retail + property fleets</p></article>
      <article class="category-card"><span class="category-icon">AI</span><h3>AI Cameras</h3><p>Retail + monitoring</p></article>
      <article class="category-card"><span class="category-icon">3D</span><h3>3D Printers</h3><p>Direct retail</p></article>
      <article class="category-card"><span class="category-icon">CB</span><h3>Cobots &amp; Arms</h3><p>Quote + integration</p></article>
      <article class="category-card"><span class="category-icon">WR</span><h3>Warehouse Robots</h3><p>Managed assets</p></article>
      <article class="category-card"><span class="category-icon">EN</span><h3>Solar &amp; Batteries</h3><p>Retail + projects</p></article>
      <article class="category-card"><span class="category-icon">AV</span><h3>Autonomous Vehicles</h3><p>Managed transportation</p></article>
    </div>
  </section>

  <section class="commercial-ladder" aria-labelledby="ladder-heading">
    <span class="section-kicker">ANYSPACE commercial ladder</span>
    <h2 id="ladder-heading">A Path from Discovery to Managed Infrastructure</h2>
    <div class="ladder-track">
      <span>Content</span><strong>→</strong><span>Retail</span><strong>→</strong><span>Professional Sale</span><strong>→</strong><span>Managed Asset</span><strong>→</strong><span>Managed Fleet</span><strong>→</strong><span class="ladder-core">Agent Server</span>
    </div>
    <a routerLink="/contact" [queryParams]="{ source: 'retail-smart-assets', type: 'retail' }" class="btn btn-primary final-cta">Discuss Smart Assets</a>
  </section>
</main>
